import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, PageHeader, Spinner } from '@billda/design-system';
import { PermissionGate } from '../../../shared/components/gates/PermissionGate';
import { Permissions } from '@billda/permissions-ui';
import { PageBreadcrumb } from '../../../shared/components/layout/PageBreadcrumb';
import { useUser } from '../hooks/useUser';
import { getUserDisplayName } from '../utils/userDisplayUtils';
import { UserOverviewCard } from '../components/panel/molecules/UserOverviewCard';
import { UserRoleCard } from '../components/panel/molecules/UserRoleCard';
import { UserBusinessDetailsCard } from '../components/panel/molecules/UserBusinessDetailsCard';
import { UserBusinessDetailsEmptyCard } from '../components/panel/molecules/UserBusinessDetailsEmptyCard';
import { UserPaymentDetailsCard } from '../components/panel/molecules/UserPaymentDetailsCard';
import { UserPaymentDetailsEmptyCard } from '../components/panel/molecules/UserPaymentDetailsEmptyCard';
import { UserLabourGroupCard } from '../components/panel/molecules/UserLabourGroupCard';
import { UserSiteAccessCard } from '../components/panel/molecules/UserSiteAccessCard';
import { UserInvitationCard } from '../components/panel/molecules/UserInvitationCard';
import { UserOffboardingCard } from '../components/panel/molecules/UserOffboardingCard';
import { useCurrentUser } from '../../auth/hooks/useCurrentUser';
import { UserStatus } from '../api/userManagementApi';
import { Routes } from '../../../routes';
import {
  formatDate,
  formatDateTime,
} from '../../../shared/utils/dateFormatting';
import { UserStatusBadge } from '../components/panel/atoms/UserStatusBadge';

type EditingSection = 'role' | 'sites' | 'labour' | null;

function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const { data: user, isLoading, error } = useUser(userId ?? null);
  const currentUser = useCurrentUser();
  const [editingSection, setEditingSection] = useState<EditingSection>(null);
  const isCurrentUser = !!(
    user &&
    currentUser &&
    user.id === currentUser.userId
  );

  if (isLoading) {
    return (
      <div className="flex h-full flex-col" data-testid="user-detail-page">
        <PageHeader
          title="Loading user"
          breadcrumbs={
            <PageBreadcrumb
              segments={[
                { label: 'Users', to: Routes.users.manage },
                { label: 'Loading...' },
              ]}
            />
          }
        />
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex h-full flex-col" data-testid="user-detail-page">
        <PageHeader
          title="User details"
          breadcrumbs={
            <PageBreadcrumb
              segments={[
                { label: 'Users', to: Routes.users.manage },
                { label: 'Not found' },
              ]}
            />
          }
        />
        <div className="flex flex-1 items-center justify-center">
          <Alert severity="error">
            {error ? 'Failed to load user details.' : 'User not found.'}
          </Alert>
        </div>
      </div>
    );
  }

  const displayName = getUserDisplayName(user);

  return (
    <div className="flex h-full flex-col" data-testid="user-detail-page">
      <PageHeader
        title={displayName}
        breadcrumbs={
          <PageBreadcrumb
            segments={[
              { label: 'Users', to: Routes.users.manage },
              { label: displayName },
            ]}
          />
        }
        actions={user.status ? <UserStatusBadge status={user.status} /> : null}
      />

      <div className="flex-1 overflow-auto px-4 py-4 sm:px-6">
        <div className="flex max-w-[1200px] flex-col gap-6 md:flex-row">
          <div className="flex w-full shrink-0 flex-col gap-6 md:sticky md:top-4 md:w-96 md:self-start">
            <UserOverviewCard
              user={user}
              formatDate={formatDate}
              isCurrentUser={isCurrentUser}
              showIdentity={false}
            />
            <UserRoleCard
              userId={user.id}
              roles={user.roles}
              status={user.status}
              isCurrentUser={isCurrentUser}
              isEditing={editingSection === 'role'}
              onEditingChange={editing =>
                setEditingSection(editing ? 'role' : null)
              }
            />
            {user.offboardingInfo && (
              <UserOffboardingCard
                offboardingInfo={user.offboardingInfo}
                formatDateTime={formatDateTime}
              />
            )}
            {user.invitationInfo && (
              <UserInvitationCard
                invitedByUserName={user.invitationInfo.invitedByUserName}
                invitedAt={user.invitationInfo.invitedAt}
                formatDateTime={formatDateTime}
              />
            )}
          </div>

          {user.status !== UserStatus.Offboarding &&
            user.status !== UserStatus.Inactive && (
              <div className="flex min-w-0 flex-1 flex-col gap-6">
                <UserSiteAccessCard
                  userId={user.id}
                  userName={displayName}
                  isManaging={editingSection === 'sites'}
                  onManagingChange={managing =>
                    setEditingSection(managing ? 'sites' : null)
                  }
                />
                <UserLabourGroupCard
                  userId={user.id}
                  currentGroupId={user.labourGroupId}
                  currentGroupName={user.labourGroupName ?? 'Unassigned'}
                  isEditing={editingSection === 'labour'}
                  onEditingChange={editing =>
                    setEditingSection(editing ? 'labour' : null)
                  }
                />
                {user.businessDetails ? (
                  <UserBusinessDetailsCard
                    businessDetails={user.businessDetails}
                  />
                ) : (
                  <UserBusinessDetailsEmptyCard />
                )}
                <PermissionGate
                  permission={Permissions.Users.PaymentDetails.Read}
                  fallback={null}
                >
                  {user.paymentDetails ? (
                    <UserPaymentDetailsCard
                      paymentDetails={user.paymentDetails}
                    />
                  ) : (
                    <UserPaymentDetailsEmptyCard />
                  )}
                </PermissionGate>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

export default UserDetailPage;
